<script lang="ts">
    import { openModal } from 'svelte-modals';
    import { get } from 'svelte/store';
    import { _ } from 'svelte-i18n';

    import ButtonSmallBlueRounded from '../../shared/ButtonSmallBlueRounded.svelte';
    import { type EasyVrpCalculationResponseType } from './EasyVrpCalculationApi';
    import { FriendlyDateGenerator } from '../../utilities/FriendlyDateGenerator';
    import { vehiclesManager } from '../Vehicles/VehiclesApp/vehiclesManager';
    import EasyVrpFormResultSummary from './EasyVrpFormResultSummary.svelte';
    import { EasyVrpMapContext } from './EasyVrpMap/EasyVrpMapContext';
    import ModalCustom from '../../shared/Modals/ModalCustom.svelte';
    import IconPending from '../../shared/Icons/IconPending.svelte';
    import LinkSmallBlack from '../../shared/LinkSmallBlack.svelte';
    import IconRemove from '../../shared/Icons/IconRemove.svelte';
    import type { AppOptionsType } from './EasyVrpApp.types';
    import { debug, planning } from '../../../stores/store';
    import { mapContext } from './EasyVrpMap/MapContext';
    import ModalExtraData from './ModalExtraData.svelte';
    import Switch from '../../shared/Switch.svelte';
    import { EasyVrpApp } from './EasyVrpApp';
    import { EasyVrpApi } from './EasyVrpApi';
    import { api } from '../../../stores/api';

    export let app: EasyVrpApp;
    export let result: EasyVrpCalculationResponseType;
    export let processing: boolean;

    let isResultCurrent = false;
    let isResultExpanded = false;
    let resultDetails: EasyVrpCalculationResponseType | null = null;
    let infoCalculatedTime = '';

    function getCalcResults(id: number) {
        processing = true;
        if (!id) {
            console.warn('No request ID available, cannot fetch results.');
            processing = false;
            return;
        }
        EasyVrpApi.getAutodispatchResults($api, id)
            .then(result => {
                if (result.status === 'pending') {
                    console.info('pending results:', result);
                    return;
                }
                console.log('Autodispatch results:', result);
                app.clearVrpCalcId();
                app.getResultsApp()
                    .loadResultDetails($api, id)
                    .then(() => {
                        app = app;
                        console.log('Result details loaded successfully');
                    });
                app.getResultsApp()
                    .loadResults($api)
                    .then(() => {
                        app = app; // Trigger reactivity
                        console.log('Results loaded successfully');
                    })
                    .catch(error => {
                        console.error('Error loading results:', error);
                    });
            })
            .catch(error => {
                console.error('Error fetching autodispatch results:', error);
            })
            .finally(() => {
                processing = false;
            });
    }

    function showResult(resultId: number, selectedRouteId: string | null = null) {
        processing = true;
        app.getResultsApp()
            .loadResultDetails($api, resultId)
            .then(result => {
                processing = false;
                console.log('Result details loaded:', result);
                if (!result) {
                    console.warn('No result details found for ID:', resultId);
                    return;
                }
                if (!result.routes_to_draw) {
                    console.warn('No routesToDraw data found in result:', result);
                    return;
                }
                if (!result.extra_data) {
                    console.warn('No extraData found in result:', result);
                    return;
                }
                mapContext.set(
                    new EasyVrpMapContext(
                        result.id,
                        result.routes_to_draw,
                        $vehiclesManager.getItems(),
                        result.extra_data
                    )
                    .setSelectedRouteId(selectedRouteId)
                );
                if (get(mapContext) !== null) {
                    app.getMapManager().drawMapElements(get(mapContext));
                    app = app;
                }
            })
            .catch(error => {
                console.error('Error loading result details:', error);
                processing = false;
            });
    }

    function showExtraData(extra_data: AppOptionsType) {
        openModal(ModalCustom, {
            component: ModalExtraData,
            componentProps: {
                extra_data,
            },
        });
    }

    function getTimeDiff(timestampFrom: string | null, timestampTo: string | null): string {
        if (!timestampFrom || !timestampTo) {
            return '---';
        }
        const from = new Date(timestampFrom);
        const to = new Date(timestampTo);
        const diff = to.getTime() - from.getTime();
        const minutes = Math.floor(diff / 1000 / 60);
        if (minutes < 1 && minutes >= 0) {
            return 'poniżej 1 minuty';
        }
        return `${minutes} min`;
    }

    $: isResultCurrent = result?.id === app.getResultsApp().getResultDetails()?.id;
    $: resultDetails = app.getResultsApp().getResultDetails();
    $: isResultExpanded = isResultExpanded;
    $: infoCalculatedTime =
        'Rozpoczęto obliczenia: ' +
        result?.created +
        '\nZakończono obliczenia: ' +
        (result?.calculated ?? '---') +
        '\nw czasie ' +
        getTimeDiff(result?.created ?? null, result?.calculated ?? null);
</script>

<div
    style="display: flex; flex-direction: column; border-bottom: 1px solid #ccc; "
    class:selected={isResultExpanded}
>
    <div
        style="display: flex; flex-direction: row; justify-content: space-between; gap: 5px; padding: 10px; align-items: center;"
    >
        <div
            style="display: flex; flex-direction: row; justify-content: flex-start; gap: 10px; align-items: center;"
        >
            <Switch
                label="Kalkulacja"
                bind:checked={isResultExpanded}
                disabled={processing || result?.calculated === null}
            />
            <div style="font-weight: 600;">#{result.id}</div>
            <!--            <div>({result.extra_data?.solver_type ?? '-&#45;&#45;'})</div>-->
            <div>
                <LinkSmallBlack
                    text={result.extra_data?.solver_type ?? '---'}
                    on:click={() => {
                        if (result?.extra_data) {
                            showExtraData(result?.extra_data);
                        } else {
                            console.warn('No extra data available for this result.');
                        }
                    }}
                />
            </div>
            <!--            <div>{result.calculated}</div>-->
            <div title="Koszt obliczeń">
                Koszt: <span style="font-weight: 600;"
                    >{result.summary?.cost ? Math.round(result.summary?.cost) : '---'}</span
                >
            </div>
            <div class="underline-title" title={infoCalculatedTime}>
                {$_(new FriendlyDateGenerator(result?.created).getRelativeTodayText(true))}
            </div>
        </div>
        <div>
            <div style="display: flex; flex-direction: row; gap: 5px; align-items: center;">
                <div>
                    <IconRemove
                        scale={0.6}
                        hover="Usuń"
                        on:click={e => {
                            e.stopPropagation();
                            processing = true;
                            app.getResultsApp()
                                .deleteResult($api, result.id)
                                .then(() => {
                                    app = app;
                                    processing = false;
                                })
                                .catch(error => {
                                    console.error('Error deleting result:', error);
                                    processing = false;
                                });
                        }}
                    />
                </div>
                {#if result?.calculated === null && result?.error === null}
                    <div>
                        W trakcie obliczeń
                        {#if (result?.progress_float * 100).toFixed(0) !== '0'}({(
                                result?.progress_float * 100
                            ).toFixed(0) ?? 0}%)
                        {/if}
                    </div>
                    <div>
                        <IconPending spin={true} scale={0.6} />
                    </div>
                {:else if result?.error === null}
                    <ButtonSmallBlueRounded
                        text={$_('Apply')}
                        colorMode={result?.assigned ? 'green' : 'blue'}
                        disabled={processing || result?.calculated === null}
                        on:click={e => {
                            e.stopPropagation();
                            processing = true;
                            app.assignCalculation($api, result.id, $planning.selectedDay)
                                .then(_ => {
                                    console.log('Calculation assigned successfully', _);
                                    app = app; // Trigger reactivity
                                })
                                .catch(err => {
                                    console.error(err);
                                    alert('Nie udało się przypisać zleceń z tej kalkulacji. ' + err);
                                })
                                .finally(() => {
                                    processing = false;
                                });
                        }}
                    />
                    <ButtonSmallBlueRounded
                        colorMode="black"
                        text="Pokaż na mapie"
                        disabled={processing || result?.calculated === null || isResultCurrent}
                        on:click={e => {
                            e.stopPropagation();
                            showResult(result.id);
                        }}
                    />
                {:else}
                    <div style="color: red;">
                        Błąd: nie udało sie wykonać obliczeń dla podanych danych.
                    </div>
                    {#if $debug}
                        <div>
                            <pre>{result.error}</pre>
                        </div>
                    {/if}
                {/if}
            </div>
        </div>
    </div>
    {#if isResultExpanded}
        <div
            style="display: flex; flex-direction: column; gap: 10px; padding: 10px; justify-content: center; align-items: center;"
        >
            {#if $debug}
                <div>
                    COST: {JSON.stringify(result?.summary ?? {}, null, 2)}
                </div>
            {/if}
            <EasyVrpFormResultSummary {result} {showResult} />
        </div>
    {/if}
</div>

<style>
    .processing {
        opacity: 0.5;
        pointer-events: none;
    }
    .selected {
        background-color: var(--main-selected-background);
        border-color: #007bff;
    }
    .clickable {
        cursor: pointer;
    }
    .clickable:hover {
        color: #1b5abf;
        text-decoration: underline;
    }
    .underline-title {
        opacity: 0.8;
        text-decoration: underline dotted;
        cursor: pointer;
    }
</style>
